<template>
  <div class="p-3 pb-16 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- Header -->
    <div class="header">
      <h1 class="header-title">บันทึกการกระทำของผู้เล่น</h1>
      <div class="filter-controls">
        <label class="toggle-label">
          <input type="checkbox" v-model="useUidFilter" />
          กรองตาม UID ผู้เล่น
        </label>
        <div v-if="useUidFilter" class="search-container">
          <input 
            v-model="searchUid" 
            placeholder="กรอก UID ผู้เล่น" 
            class="search-input"
          />
          <span class="search-icon">🔍</span>
        </div>
        <input 
          v-model="filterAction" 
          placeholder="พิมพ์การกระทำเพื่อค้นหา" 
          class="filter-input"
        />
        <button @click="handleSearch" class="search-button">ค้นหา</button>
      </div>
    </div>

    <!-- Logs List -->
    <div class="logs-list">
      <div v-if="filteredLogs.length === 0" class="empty-state">
        <p>ไม่พบประวัติการกระทำ</p>
      </div>
      
      <div 
        v-for="log in filteredLogs" 
        :key="log.id" 
        class="log-card"
        :class="{ 'success': log.success, 'error': !log.success }"
      >
        <div class="log-content">
          <div class="log-header">
            <span class="log-action">{{ getActionName(log.action) }}</span>
            <span class="log-timestamp">{{ formatTimestamp(log.timestamp) }}</span>
          </div>

          <p class="log-message">{{ log.displayMessage || 'ไม่มีข้อความแสดง' }}</p>

          <!-- แสดงรายละเอียดแบบปลอดภัย -->
          <div class="log-details">
            <p>UID: {{ log.uid || 'ไม่ระบุ' }}</p>

            <template v-if="log?.details">
              <p v-if="log.details.characterName">ชื่อตัวละคร: {{ log.details.characterName }}</p>
              <p v-if="log.details.traitName">ลักษณะนิสัย: {{ log.details.traitName }}</p>
              <p v-if="log.details.usedPoints != null">แต้มที่ใช้: {{ log.details.usedPoints }}</p>

              <!-- ถ้าต้องการแสดงรายละเอียดอื่นๆ เพิ่มตรงนี้ได้เลย -->
              <!-- ตัวอย่าง: -->
              <!-- <p v-if="log.details.mapName">แผนที่: {{ log.details.mapName }}</p> -->
              <!-- <p v-if="log.details.gainedExp">EXP ที่ได้: {{ log.details.gainedExp }}</p> -->

              <!-- หรือจะแสดงทั้งหมดแบบ JSON (สำหรับ debug) -->
              <pre v-if="log.details" class="debug-json">{{ JSON.stringify(log.details, null, 2) }}</pre>
            </template>

            <p v-else class="no-details">ไม่มีรายละเอียดเพิ่มเติม</p>

            <p v-if="log?.error && !log.success" class="error-message">
              ข้อผิดพลาด: {{ log.error }}
            </p>
          </div>
        </div>
      </div>

      <!-- Load More Button -->
      <div v-if="hasMoreLogs && filteredLogs.length > 0" class="load-more-container">
        <button @click="loadMore" class="load-more-button">โหลดเพิ่ม</button>
      </div>
    </div>
  </div>
</template>
<script>
import { projectFirestore } from '@/firebase/config';
import { ref, computed, watch } from 'vue';

export default {
  setup() {
    const logs = ref([]);
    const filterAction = ref('');
    const searchUid = ref('');
    const useUidFilter = ref(false);
    const currentUid = ref(''); // Track the current UID used for fetching
    const currentAction = ref(''); // Track the current action used for fetching
    const lastDoc = ref(null); // Track the last document for pagination
    const hasMoreLogs = ref(true); // Track if there are more logs to load
    const pageSize = 1000; // Number of logs to fetch per page

    // Fetch logs for specific UID or all if UID is empty, and specific action if provided
    const fetchLogs = async (uid, action, isLoadMore = false) => {
      try {
        let query = projectFirestore.collection('logs');

        if (uid) {
          query = query.where('uid', '==', uid);
        }

        if (action) {
          query = query.where('action', '==', action);
        }

        query = query.orderBy('timestamp', 'desc').limit(pageSize);

        // If loading more, start after the last document
        if (isLoadMore && lastDoc.value) {
          query = query.startAfter(lastDoc.value);
        }

        const snapshot = await query.get();

        const newLogs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Update logs: append if load more, replace if initial fetch
        if (isLoadMore) {
          logs.value = [...logs.value, ...newLogs];
        } else {
          logs.value = newLogs;
        }

        // Update last document and hasMoreLogs
        lastDoc.value = snapshot.docs[snapshot.docs.length - 1] || null;
        hasMoreLogs.value = snapshot.docs.length === pageSize;
      } catch (error) {
        console.error('Error fetching logs:', error);
        logs.value = isLoadMore ? logs.value : [];
        hasMoreLogs.value = false;
      }
    };

    // Load more logs
    const loadMore = () => {
      fetchLogs(currentUid.value, currentAction.value, true);
    };

    // Format timestamp
    const formatTimestamp = (timestamp) => {
      if (!timestamp) return 'ไม่ระบุ';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('th-TH', {
        dateStyle: 'medium',
        timeStyle: 'short'
      });
    };

    // Get action name
    const getActionName = (action) => {
      const actions = {
        create_character: 'สร้างตัวละคร',
        upgrade_stats: 'อัปเกรดสเตตัส'
      };
      return actions[action] || action;
    };

    // Filtered logs (now just logs since filtering is server-side)
    const filteredLogs = computed(() => {
      return logs.value;
    });

    // Handle search button click
    const handleSearch = () => {
      currentUid.value = useUidFilter.value ? searchUid.value.trim() : '';
      currentAction.value = filterAction.value.trim();
      lastDoc.value = null; // Reset pagination on new search
      hasMoreLogs.value = true;
      fetchLogs(currentUid.value, currentAction.value, false);
    };

    // Watch for changes in filterAction
    watch(filterAction, () => {
      handleSearch();
    });

    return {
      logs,
      filterAction,
      searchUid,
      useUidFilter,
      filteredLogs,
      formatTimestamp,
      getActionName,
      loadMore,
      hasMoreLogs,
      handleSearch
    };
  }
}
</script>

<style scoped>
.admin-logs-container {
  min-height: 100vh;
  padding: 1.5rem;
  background: linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9)),
              url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1') no-repeat center center fixed;
  background-size: cover;
  overflow-y: auto;
  font-family: 'Sarabun', sans-serif;
  color: #e5e7eb;
}

.header {
  margin-bottom: 2rem;
  text-align: center;
}

.header-title {
  font-size: 2.25rem;
  font-weight: bold;
  color: #f3f4f6;
  margin-bottom: 1.5rem;
}

.filter-controls {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  align-items: center;
}

.toggle-label {
  display: flex;
  align-items: center;
  color: #e5e7eb;
  font-size: 1rem;
}

.filter-input {
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  background: #374151;
  color: #e5e7eb;
  border: 1px solid #4b5563;
  transition: border-color 0.2s;
}

.filter-input:focus {
  border-color: #60a5fa;
  outline: none;
}

.search-container {
  position: relative;
  width: 250px;
}

.search-input {
  width: 100%;
  padding: 0.5rem 2.5rem 0.5rem 1rem;
  border-radius: 0.375rem;
  background: #374151;
  color: #e5e7eb;
  border: 1px solid #4b5563;
  transition: border-color 0.2s;
}

.search-input:focus {
  border-color: #60a5fa;
  outline: none;
}

.search-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.search-button {
  padding: 0.5rem 1.5rem;
  background: #60a5fa;
  color: #fff;
  border: none;
  border-radius: 0.375rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
}

.search-button:hover {
  background: #3b82f6;
}

.logs-list {
  max-width: 900px;
  margin: 0 auto;
}

.log-card {
  background: rgba(55, 65, 81, 0.95);
  border-radius: 0.5rem;
  padding: 1.25rem;
  margin-bottom: 1rem;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.log-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.log-card.success {
  border-left: 5px solid #10b981;
}

.log-card.error {
  border-left: 5px solid #ef4444;
}

.log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.log-action {
  font-weight: 600;
  color: #60a5fa;
  font-size: 1.1rem;
}

.log-timestamp {
  font-size: 0.9rem;
  color: #9ca3af;
}

.log-message {
  margin-bottom: 0.75rem;
  color: #e5e7eb;
  font-size: 1rem;
  line-height: 1.5;
}

.log-details {
  font-size: 0.9rem;
  color: #d1d5db;
}

.log-details p {
  margin: 0.3rem 0;
}

.error-message {
  color: #f87171;
  font-style: italic;
}

.empty-state {
  text-align: center;
  color: #9ca3af;
  padding: 2.5rem;
  background: rgba(55, 65, 81, 0.7);
  border-radius: 0.5rem;
  margin: 2rem 0;
}

.load-more-container {
  text-align: center;
  margin-top: 1.5rem;
}

.load-more-button {
  padding: 0.75rem 2rem;
  background: #60a5fa;
  color: #fff;
  border: none;
  border-radius: 0.375rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
}

.load-more-button:hover {
  background: #3b82f6;
}

.load-more-button:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.5);
}

@media (max-width: 768px) {
  .admin-logs-container {
    padding: 1rem;
  }

  .header-title {
    font-size: 1.75rem;
  }

  .filter-controls {
    flex-direction: column;
    align-items: center;
  }

  .search-container {
    width: 100%;
  }

  .log-card {
    padding: 1rem;
  }
}
</style>